<template>
  <div>
    <div class="row" :class="{'disabled-form': creatingPaymentMethod}">
      <div id="customCard-container">
        <div class="col s12 l6">
          <div class="custom-card-field">
            <label>Card number</label>
            <span data-cse="encryptedCardNumber" class="adyen-encrypted-field"></span>
          </div>
        </div>

        <div class="col s4 l2">
          <div class="custom-card-field">
            <label>Exp date</label>
            <span data-cse="encryptedExpiryDate" class="adyen-encrypted-field"></span>
          </div>
        </div>

        <div class="col s4 l2">
          <div class="custom-card-field">
            <label>CVC</label>
            <span data-cse="encryptedSecurityCode" class="adyen-encrypted-field"></span>
          </div>
        </div>

        <div class="col s4 l2">
          <div class="custom-card-field">
            <label for="addressZip">{{ zipLabel }}</label>
            <input id="addressZip" ref="addressZip" class="zip-input" :class="{'disabled-input': creatingPaymentMethod}" type="text" v-mask="zipMask" v-model="addressZip"
              :style="{ color: foregroundColor }" placeholder="90210" v-validate="'required|length:' + zipLength"
              @input="onZipChange" data-vv-name="postalCode" :disabled="creatingPaymentMethod" />
          </div>
        </div>
      </div>
    </div>

    <div v-if="creatingPaymentMethod" class="!verification-status center">
      <i class="fas fa-spinner fa-spin grey-text"></i>
      <span class="grey-text">Setting up payment method...</span>
    </div>

    <div id="card-errors" class="red-text text-darken-3">{{ cardError }}</div>
    <div class='red-text'>{{ errorMessage }}</div>
    <div class='red-text'>{{ processingErrorMessage }}</div>
  </div>
</template>
<script>
import gql from 'graphql-tag'

export default {
  props: {
    qid: {
      type: String,
      required: true
    },
    clientKey: {
      type: String,
      required: true
    },
    presetAddressZip: {
      type: String,
      default: ''
    },
    foregroundColor: {
      type: String,
      default: '#32325d'
    },
    // When true, uses REST endpoints instead of GraphQL (for unauthenticated users)
    publicMode: {
      type: Boolean,
      default: false
    },
  },
  async mounted() {
    this.$bus.$on('regenerate-card-token', this.regenerateToken)
    // A ZIP preset from the link never fires @input, so without this it never
    // counts as complete and Pay stays disabled until the payer retypes it
    if (this.addressZip) {
      this.onZipChange()
    }
    await this.initializeAdyen()
  },
  beforeDestroy() {
    this.$bus.$off('regenerate-card-token', this.regenerateToken)
    if (this.checkout) {
      this.unmountComponents()
    }
  },
  data() {
    return {
      errorMessage: null,
      processingErrorMessage: null,
      cardError: null,
      cardNumberComplete: false,
      cardExpiryComplete: false,
      cardCvcComplete: false,
      postalCodeComplete: false,
      addressZip: this.presetAddressZip || "",
      creatingPaymentMethod: false,
      checkout: null,
      cardNumber: null,
      cardStateData: null
    }
  },
  methods: {
    async initializeAdyen() {
      await this.loadAdyenJs()
      await this.setupAdyen()
    },

    getAdyenEnvironment() {
      // Determine environment from client key prefix
      if (!this.clientKey) return 'test'
      return this.clientKey.startsWith('live_') ? 'live' : 'test'
    },

    loadAdyenJs() {
      return new Promise((resolve, reject) => {
        const environment = this.getAdyenEnvironment()
        const baseUrl = environment === 'live'
          ? 'https://checkoutshopper-live.adyen.com/checkoutshopper/sdk/5.59.0'
          : 'https://checkoutshopper-test.adyen.com/checkoutshopper/sdk/5.59.0'

        const cssLink = document.createElement('link')
        cssLink.setAttribute('rel', 'stylesheet')
        cssLink.setAttribute('href', `${baseUrl}/adyen.css`)
        document.head.appendChild(cssLink)

        this.script = document.createElement('script')
        this.script.setAttribute('src', `${baseUrl}/adyen.js`)
        this.script.onload = () => resolve()
        this.script.onerror = () => reject(new Error('Failed to load Adyen script'))
        document.head.appendChild(this.script)
      })
    },

    async setupAdyen() {
      const environment = this.getAdyenEnvironment()

      // Check if AdyenCheckout is available
      if (typeof AdyenCheckout === 'undefined') {
        console.error('AdyenCheckout is not defined - script may not have loaded')
        this.processingErrorMessage = 'Failed to load Adyen payment system'
        return
      }

      // Check if we're in a secure context
      if (!window.isSecureContext) {
        console.error('Not in secure context - Web Crypto API may not be available')
        this.processingErrorMessage = 'Payment system requires secure connection (HTTPS or localhost)'
        return
      }

      const configuration = {
        environment: environment,
        clientKey: this.clientKey
      }

      try {
        this.checkout = await AdyenCheckout(configuration)
      } catch (error) {
        console.error('Error creating AdyenCheckout instance:', error)
        this.processingErrorMessage = `Failed to initialize payment system: ${error.message}`
        return
      }
      this.setupCardComponents()
    },

    setupCardComponents() {
      const cardConfig = {
        type: 'card',
        brands: ['visa', 'mc', 'amex', 'discover', 'diners', 'jcb', 'cup'],
        autoFocus: true,
        styles: {
          base: {
            color: this.foregroundColor,
            fontSize: '14px',
            fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif'
          },
          error: {
            color: '#fa755a'
          },
          placeholder: {
            color: '#aab7c4'
          }
        },
        onFieldValid: (state) => {
          this.onFieldValid(state)
        },
        onChange: (state) => {
          this.onChange(state)
        },
        onError: (error) => {
          console.error('Secured fields error:', error)
          this.cardError = error.error || 'Card validation error'
        }
      }

      // Create custom card component with secured fields
      this.cardNumber = this.checkout.create('securedfields', cardConfig)

      // Mount to the container with data-cse elements
      this.cardNumber.mount('#customCard-container')
    },

    onFieldValid(state) {
      if (state.fieldType === "encryptedCardNumber" && state.valid) {
        this.$nextTick(() => {
          this.cardNumber.setFocusOn("encryptedExpiryDate");
        })
      }
      // When CVC field becomes valid, focus ZIP
      if (state.fieldType === 'encryptedSecurityCode' && state.valid) {
        this.$nextTick(() => {
          this.$refs.addressZip.focus()
        })
      }
    },

    onChange(state) {
      if (state.errors && state.errors.length > 0) {
        this.cardError = state.errors[0]
      } else {
        this.cardError = null
      }

      // Check individual field validity from state.valid object
      this.cardNumberComplete = state.valid?.encryptedCardNumber || false
      this.cardExpiryComplete = state.valid?.encryptedExpiryDate || state.valid?.encryptedExpiryMonth || false
      this.cardCvcComplete = state.valid?.encryptedSecurityCode || false

      // Store card state data including encrypted fields
      this.cardStateData = state.data

      this.checkAndSubmitPayment()
    },

    onZipChange() {
      this.$validator.validate('postalCode', this.addressZip).then((valid) => {
        this.postalCodeComplete = valid
        this.checkAndSubmitPayment()
      })
    },

    checkAndSubmitPayment() {
      if (this.cardNumberComplete && this.cardExpiryComplete &&
        this.cardCvcComplete && this.postalCodeComplete &&
        !this.creatingPaymentMethod) {
        this.submitPayment()
      } else if (!this.creatingPaymentMethod) {
        this.$emit('token-generated', {token: null, funding: null})
      }
    },

    regenerateToken() {
      if (this.cardNumberComplete && this.cardExpiryComplete &&
        this.cardCvcComplete && this.postalCodeComplete) {
        this.submitPayment()
      }
    },

    async submitPayment() {
      if (this.creatingPaymentMethod) {
        return
      }

      if (!this.cardStateData) {
        this.processingErrorMessage = 'Card data not ready'
        return
      }

      this.creatingPaymentMethod = true

      if (this.publicMode) {
        return this.submitPaymentRest()
      }
      return this.submitPaymentGraphQL()
    },

    async submitPaymentRest() {
      try {
        const response = await this.$http.post('/api/v1/public/adyen_tokens', {
          qid: this.qid,
          encrypted_card_number: this.cardStateData.paymentMethod.encryptedCardNumber,
          encrypted_expiry_month: this.cardStateData.paymentMethod.encryptedExpiryMonth,
          encrypted_expiry_year: this.cardStateData.paymentMethod.encryptedExpiryYear,
          encrypted_security_code: this.cardStateData.paymentMethod.encryptedSecurityCode,
          postal_code: this.addressZip
        })

        this.creatingPaymentMethod = false
        this.processingErrorMessage = null

        if (!response.body) {
          this.processingErrorMessage = "Authorization failed"
          this.$emit('token-generated', {token: null, funding: null})
          return
        }
        if (response.body.errors && response.body.errors.length > 0) {
          this.processingErrorMessage = response.body.errors[0]
          this.$emit('token-generated', {token: null, funding: null})
        } else {
          this.$emit('token-generated', {
            token: response.body.token,
            funding: response.body.funding
          })
        }
      } catch (error) {
        this.creatingPaymentMethod = false
        this.processingErrorMessage = `Error: ${error.message || 'Unknown error'}`
        this.$emit('token-generated', {token: null, funding: null})
      }
    },

    submitPaymentGraphQL() {
      this.$apollo.mutate({
        mutation: gql`mutation ($input: TokenizeEncryptedCardInput!) {
          tokenizeEncryptedCard(input: $input) {
            token
            funding
            errors
          }
        }`,
        variables: {
          input: {
            encryptedCardNumber: this.cardStateData.paymentMethod.encryptedCardNumber,
            encryptedExpiryMonth: this.cardStateData.paymentMethod.encryptedExpiryMonth,
            encryptedExpiryYear: this.cardStateData.paymentMethod.encryptedExpiryYear,
            encryptedSecurityCode: this.cardStateData.paymentMethod.encryptedSecurityCode,
            postalCode: this.addressZip
          }
        }
      }).then(result => {
        this.creatingPaymentMethod = false
        this.processingErrorMessage = null

        const tokenizeResult = result.data.tokenizeEncryptedCard
        if (!tokenizeResult) {
          this.notifyError("Authorization failed")
          this.processingErrorMessage = "Authorization failed"
          this.$emit('token-generated', {token: null, funding: null})
          return
        }
        if (tokenizeResult.errors.length > 0) {
          this.notifyError(tokenizeResult.errors[0])
          this.processingErrorMessage = tokenizeResult.errors[0]
          this.$emit('token-generated', {token: null, funding: null})
        } else {
          this.$emit('token-generated', {
            token: tokenizeResult.token,
            funding: tokenizeResult.funding
          })
        }
      }, error => {
        this.creatingPaymentMethod = false
        this.notifyError("An error occurred while saving the payment method.")
        this.processingErrorMessage = `Error: ${error.message || 'Unknown error'}`
        this.$emit('token-generated', {token: null, funding: null})
      })
    },

    unmountComponents() {
      if (this.cardNumber) {
        this.cardNumber.unmount()
      }
    }
  },
  computed: {
    isPartialCanadianZip() {
      return /^[A-Za-z]/.test(this.addressZip)
    },
    isPartialUSZip() {
      return /^[0-9]{1,5}(-[0-9]{1,4})?$/.test(this.addressZip)
    },
    zipLength() {
      if (this.isPartialCanadianZip) {
        return 7
      } else if (this.isPartialUSZip) {
        return 5
      } else {
        return 1000
      }
    },
    zipLabel() {
      if (this.isPartialCanadianZip) {
        return "Zip (CAN)"
      } else if (this.isPartialUSZip) {
        return "Zip (US)"
      } else {
        return "Zip"
      }
    },
    zipMask() {
      if (this.isPartialCanadianZip) {
        return "A#A #A#"
      } else if (this.isPartialUSZip) {
        return "#####"
      } else {
        return "#####"
      }
    }
  }
}
</script>
<style>
.adyen-input {
  display: block;
  margin-top: 5px;
  padding: 10px;
  border: none;
  border-radius: 5px;
}

.zip-input {
  display: block !important;
  margin-top: 5px !important;
  padding: 10px !important;
  border: 1px solid #ccc !important;
  border-radius: 5px !important;
  font-size: 14px !important;
  box-sizing: border-box !important;
  outline: none !important;
  height: auto !important;
}

.zip-input::placeholder {
  color: #AAB7C4;
}

.zip-input::-webkit-input-placeholder {
  color: #AAB7C4;
}

.zip-input::-moz-placeholder {
  color: #AAB7C4;
  opacity: 1;
}

.zip-input:-ms-input-placeholder {
  color: #AAB7C4;
}

.zip-input:-moz-placeholder {
  color: #AAB7C4;
  opacity: 1;
}

.zip-input:focus {
  border-bottom: 1px solid #ccc !important;
  box-shadow: none !important;
}

.zip-input.invalid {
  border-bottom: 1px solid #ccc !important;
  box-shadow: none !important;
}

.zip-input.valid {
  border-bottom: 1px solid #ccc !important;
  box-shadow: none !important;
}

.disabled-form {
  opacity: 0.7;
  pointer-events: none;
}

.disabled-input {
  background-color: #f5f5f5 !important;
  cursor: not-allowed !important;
}

.adyen-checkout__card__brands,
.adyen-checkout__card__cardNumber__brandIcon {
  display: none !important;
}

.adyen-checkout__field__disclaimer,
.adyen-checkout__card__form__disclaimer,
.adyen-checkout__label__text--disclaimerMessage,
.adyen-checkout__disclaimer,
.adyen-checkout-form-instruction,
[class*="disclaimer"] {
  display: none !important;
}

#customCard-container {
  width: 100%;
}

.custom-card-field {
  margin-bottom: 15px;
}

.custom-card-field label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
}

.adyen-encrypted-field {
  display: block;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  min-height: 40px;
  max-height: 40px;
  background-color: white;
  overflow: hidden;
}

.adyen-encrypted-field iframe {
  border: none !important;
  height: 20px !important;
  max-height: 20px !important;
}
</style>
